import { Formik, FormikProps } from "formik";
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import { Button, Col, Form, Modal, Row } from "react-bootstrap";
import { createPatch } from "rfc6902";
import * as yup from "yup";
import { FormikInput } from "../../component-library";
import { useAwsFiles } from "../../contexts";
import { Tenant } from "../../models";
import { useAppSettingsQuery, useAwsFileQuery, useTenantQuery } from "../../queries";

const PartnerFormSchema = yup.object({
    name: yup.string().max(50, "Must be 50 characters or less").required("Required"),
    logoUri: yup.string(),
});

type PartnerFormModel = yup.InferType<typeof PartnerFormSchema>;

const InvalidFileType = "File type is not valid";
const FileInputDefault = "Choose a file...";

interface PartnerFormProps {
    onClose: VoidFunction;
    partner?: Tenant;
}

export const PartnerForm = ({ onClose, partner }: PartnerFormProps) => {
    const [hasSubmitted, setHasSubmitted] = useState(false);
    const [filePath, setFilePath] = useState(FileInputDefault);

    const {
        myAppSettingsQuery: { data: appSettings },
    } = useAppSettingsQuery();

    const { awsFiles, clearAwsFiles } = useAwsFiles();
    const {
        createMutation: { mutate: uploadAwsFile },
    } = useAwsFileQuery();

    const {
        createMutation: { mutate: createTenant, isLoading: creatingTenant, isSuccess: createSuccess },
        updateMutation: { mutate: updateTenant, isLoading: updatingTenant, isSuccess: updateSuccess },
    } = useTenantQuery({ id: partner?.id });

    const isMutating = useMemo<boolean>(() => creatingTenant || updatingTenant, [creatingTenant, updatingTenant]);

    const initialValues = useMemo<PartnerFormModel>(
        () => ({
            name: partner?.name ?? "",
            logoUri: partner?.logoUri ?? "",
        }),
        [partner],
    );

    const stripFilePath = useCallback((path: string) => {
        return path.split("\\").pop()!;
    }, []);

    const isExtensionValid = useCallback((path: string) => {
        const extension = path.substring(path.lastIndexOf(".") + 1).toLowerCase();
        return extension === "gif" || extension === "png" || extension === "jpeg" || extension === "jpg";
    }, []);

    const handleFileInputChange = useCallback(
        (file: File) => {
            if (file) {
                if (isExtensionValid(file.name)) {
                    const reader = new FileReader();

                    reader.onload = (event) =>
                        uploadAwsFile({
                            filename: file.name,
                            content: reader.result?.toString().split(",")[1] ?? "",
                            url: "",
                        });

                    reader.readAsDataURL(file);

                    if (reader.DONE === 2) {
                        return stripFilePath(file.name);
                    }

                    return FileInputDefault;
                }

                if (file.name === "") {
                    return FileInputDefault;
                }
            }

            return InvalidFileType;
        },
        [isExtensionValid, stripFilePath, uploadAwsFile],
    );

    const handleClose = useCallback(() => {
        clearAwsFiles();
        onClose();
    }, [clearAwsFiles, onClose]);

    const handleSubmit = useCallback(
        async (values: PartnerFormModel) => {
            setHasSubmitted(true);
            if (!partner) {
                createTenant({
                    ...values,
                    isActive: true,
                });
            } else {
                updateTenant(
                    createPatch(partner, {
                        ...partner,
                        ...values,
                    }),
                );
            }
        },
        [createTenant, partner, updateTenant],
    );

    useEffect(() => {
        if (!hasSubmitted) return;
        if (createSuccess) {
            handleClose();
        }
    }, [hasSubmitted, createSuccess, handleClose]);

    useEffect(() => {
        if (!hasSubmitted) return;
        if (updateSuccess) {
            handleClose();
        }
    }, [hasSubmitted, updateSuccess, handleClose]);

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={PartnerFormSchema}
            onSubmit={handleSubmit}
            enableReinitialize={true}
        >
            {(props: FormikProps<PartnerFormModel>) => (
                <Modal show backdrop="static" keyboard={false} size="lg">
                    <Modal.Header>
                        {!partner
                            ? `Add New ${appSettings?.parentTenantName}`
                            : `Edit ${appSettings?.parentTenantName}`}
                    </Modal.Header>
                    <Modal.Body>
                        <Form id="partnerForm">
                            <Row>
                                <Col sm={4}>
                                    <FormikInput name="name" label="Name" disabled={isMutating} />
                                </Col>
                                <Col sm={4}>
                                    <Form.Group controlId="logoUri" className="mb-2">
                                        <Form.Label>Logo</Form.Label>
                                        <Form.Control
                                            disabled
                                            placeholder="Upload Image"
                                            value={
                                                !!awsFiles && !!awsFiles.length
                                                    ? awsFiles[awsFiles.length - 1].url
                                                    : props.values.logoUri
                                            }
                                        />
                                    </Form.Group>
                                </Col>
                                <Col sm={4}>
                                    <Form.Group controlId="filePath" className="mb-2">
                                        <Form.Label>{filePath}</Form.Label>
                                        <Form.Control
                                            accept="image/*"
                                            type="file"
                                            onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                                setFilePath(handleFileInputChange(e.target.files![0]))
                                            }
                                        />
                                        <img
                                            alt="Partner Logo"
                                            className="mt-3"
                                            width="200px"
                                            src={
                                                !!awsFiles && !!awsFiles.length
                                                    ? awsFiles[awsFiles.length - 1].url
                                                    : props.values.logoUri
                                            }
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                        </Form>
                    </Modal.Body>
                    <Modal.Footer className="d-flex justify-content-between">
                        <Button variant="secondary" onClick={handleClose} disabled={isMutating}>
                            Cancel
                        </Button>
                        <Button
                            onClick={() => props.handleSubmit()}
                            className="ms-2"
                            disabled={isMutating || !props.dirty}
                        >
                            Submit
                        </Button>
                    </Modal.Footer>
                </Modal>
            )}
        </Formik>
    );
};
